import type { TimelineEvent } from '../../types';
import type { DxReviewState, DxReviewHistoryEntry } from '../types';

export interface DraftReviewInfo {
    state: DxReviewState;
    hasRequestChangesHistory: boolean;
}

/**
 * Extracts the patientFileId of the rendered Dx-report PDF from a
 * dx_report_submission timeline event payload. Returns null when the event
 * has no rendered PDF attached (e.g. legacy or partial events).
 */
export function getDxPatientFileId(event: TimelineEvent): string | null {
    const payload = event.payload;
    if (!payload || typeof payload !== 'object') return null;
    const files = (payload as Record<string, unknown>).files;
    if (!files || typeof files !== 'object') return null;
    const dxPdf = (files as Record<string, unknown>).dxReportPdf;
    if (!dxPdf || typeof dxPdf !== 'object') return null;
    return ((dxPdf as Record<string, unknown>).patientFileId as string) ?? null;
}

/**
 * Build a download-friendly Dx report filename in the form
 * `Dx-{patient_name}-{date}[-v{n}].pdf`. Strips whitespace and special
 * characters from the patient name to keep the filename portable.
 */
export function buildFileName(event: TimelineEvent): string {
    const payload = event.payload as Record<string, unknown> | null;
    const formData = payload?.formData as Record<string, unknown> | undefined;
    const patientInfo = formData?.patientInfo as Record<string, unknown> | undefined;
    const name = (patientInfo?.patientName as string) ?? '';
    const date = (patientInfo?.reportDate as string) ?? '';
    const sanitized = name
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^\w.-]/g, '');
    const versionSuffix = event.version > 1 ? `-v${event.version}` : '';
    return `Dx-${sanitized}-${date}${versionSuffix}.pdf`;
}

/**
 * Inspects a draft event payload and surfaces both its review state and
 * whether at least one prior `request-changes` entry exists in history -
 * the latter drives the "Changes requested" badge once the author returns
 * to editing.
 */
export function getDraftReviewInfo(draft: TimelineEvent): DraftReviewInfo {
    const payload = draft.payload as Record<string, unknown> | null;
    const formData = payload?.formData as Record<string, unknown> | undefined;
    const review = (payload?.review ?? formData?.review) as
        { state?: string; history?: DxReviewHistoryEntry[] } | undefined;
    const state = (review?.state ?? 'editing') as DxReviewState;
    const history = review?.history ?? [];
    const hasRequestChangesHistory = history.some((e) => e.action === 'request-changes');
    return { state, hasRequestChangesHistory };
}

/**
 * Compact "x minutes ago" / "x hours ago" / "x days ago" formatter for
 * the draft list. Falls back to "just now" for sub-minute deltas.
 */
export function formatRelativeTime(isoString: string): string {
    const date = new Date(isoString);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    const diffDays = Math.floor(diffHr / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}
